import { CalendarToday, Done, FileCopyOutlined, Undo, VisibilityOff } from '@mui/icons-material';
import { Box, Button, Tooltip } from '@mui/material';
import { GridRenderCellParams } from '@mui/x-data-grid-premium';
import { EstateTaskInstance, EstateTaskInstanceStatus, FeatureSubscriptionType } from 'queries';

import { useStyles } from '../task-groups.styles';

interface QuickActionsCellProps extends GridRenderCellParams {
  estateSearchTaskInstances: EstateTaskInstance[];
  groupedEstateTaskInstancesAsArray: EstateTaskInstance[];
  featureSubscriptionTypeData: { data: FeatureSubscriptionType } | null;
  handleAddDeadlineClick: (e: React.MouseEvent<HTMLButtonElement>) => void;
  handleAssignAgainClick: (instance: EstateTaskInstance) => void;
  handleHideTaskClick: () => void;
  handleToggleTaskStatus: (instance: EstateTaskInstance) => void;
  setCurrentEstateTaskInstance: (instance: EstateTaskInstance) => void;
  checkboxRefs: React.MutableRefObject<Record<string, HTMLButtonElement | null>>;
}

export const QuickActionsCell = ({
  row,
  estateSearchTaskInstances,
  groupedEstateTaskInstancesAsArray,
  featureSubscriptionTypeData,
  handleAddDeadlineClick,
  handleAssignAgainClick,
  handleHideTaskClick,
  handleToggleTaskStatus,
  setCurrentEstateTaskInstance,
  checkboxRefs,
}: QuickActionsCellProps) => {
  const classes = useStyles();
  const estateTaskInstance = row as EstateTaskInstance;
  const clientEstateTaskInstance =
    estateSearchTaskInstances.find((i) => i.id === estateTaskInstance.id)! ||
    groupedEstateTaskInstancesAsArray.find((i) => i.id === estateTaskInstance.id)!;

  const disableAssignAgain =
    !estateTaskInstance.estateWorkItemId ||
    !estateTaskInstance.userCanAssignAgain ||
    featureSubscriptionTypeData?.data === FeatureSubscriptionType.Trial;

  const disableStatusChange =
    clientEstateTaskInstance.status === EstateTaskInstanceStatus.Assigned ||
    (clientEstateTaskInstance.status === EstateTaskInstanceStatus.Completed &&
      !!clientEstateTaskInstance.estateWorkItemId);

  return (
    <Box className={classes.quickActionSection}>
      <Tooltip title="Add Deadline">
        <Button
          className={classes.quickActionButton}
          onClick={(e) => {
            setCurrentEstateTaskInstance(estateTaskInstance);
            handleAddDeadlineClick(e);
          }}
        >
          <CalendarToday fontSize="small" />
        </Button>
      </Tooltip>
      <Tooltip title="Duplicate Task">
        <Button
          className={classes.quickActionButton}
          onClick={() => handleAssignAgainClick(estateTaskInstance)}
          disabled={disableAssignAgain}
        >
          <FileCopyOutlined fontSize="small" />
        </Button>
      </Tooltip>
      <Tooltip title="Hide Task">
        <Button
          className={classes.quickActionButton}
          onClick={() => {
            setCurrentEstateTaskInstance(estateTaskInstance);
            handleHideTaskClick();
          }}
        >
          <VisibilityOff fontSize="small" />
        </Button>
      </Tooltip>
      <Tooltip
        title={
          clientEstateTaskInstance.status === EstateTaskInstanceStatus.Completed
            ? 'Undo Complete'
            : 'Mark Task as complete'
        }
      >
        <Button
          className={classes.quickActionButton}
          ref={(el) => {
            if (el) {
              checkboxRefs.current[clientEstateTaskInstance.id] = el;
            }
          }}
          disabled={disableStatusChange}
          onClick={() => handleToggleTaskStatus(clientEstateTaskInstance)}
        >
          {clientEstateTaskInstance.status === EstateTaskInstanceStatus.Completed ? (
            <Undo fontSize="small" />
          ) : (
            <Done fontSize="small" />
          )}
        </Button>
      </Tooltip>
    </Box>
  );
};
